<script>
import { getPriceRules, createPriceRule, updatePriceRule, deletePriceRule, getPriceRuleDetail } from '@src/apis/priceRule'
import { getHotelRoomTypeOptions } from '@src/apis/roomTypeOption'
import PriceAdjustmentFilters from './components/PriceAdjustmentFilters.vue'
import PriceAdjustmentTable from './components/PriceAdjustmentTable.vue'
import PriceAdjustmentDialog from './components/PriceAdjustmentDialog.vue'
import { createDialogLabels, createDialogTypeOptions, createFilterOptions, createFilterPlaceholders, createTableLabels, createTypeTextMap, createUiText } from './modules/priceAdjustment/pageConfig'
import {
  buildApiPayload,
  buildFetchParams,
  buildPatchPayload,
  getPeriodSortValue,
  getPeriodStatus,
  getSignedValue,
  isExpired,
  mapPagination,
  mapPriceRuleItem,
  normalizeRoomName,
} from './modules/priceAdjustment/pageHelpers'

const defaultFormModel = () => ({
  id: null,
  title: '',
  minNight: 2,
  type: 'stay',
  valueType: 'fixed',
  operator: '-',
  value: 0,
  roomTypeIds: [],
  dateRange: [],
  state: 1,
})

export default {
  name: 'LongStayPrice',
  components: {
    PriceAdjustmentFilters,
    PriceAdjustmentTable,
    PriceAdjustmentDialog,
  },
  props: {
    productId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,

      discounts: [],
      availableRoomTypes: [],

      filters: {
        type: '',
        operator: '',
        periodStatus: '',
        state: '',
      },

      sortProp: '',
      sortOrder: '',

      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50],
      pagination: {
        total: 0,
        currentPage: 1,
        perPage: 10,
        lastPage: 1,
      },

      dialogVisible: false,
      dialogMode: 'create',
      dialogFormModel: defaultFormModel(),
      originalDialogApiModel: null,

      uiText: createUiText(),

      filterPlaceholders: createFilterPlaceholders(),

      filterOptions: createFilterOptions(),

      typeTextMap: createTypeTextMap(),

      dialogTypeOptions: createDialogTypeOptions(),

      tableLabels: createTableLabels(),

      dialogLabels: createDialogLabels(),
    }
  },
  computed: {
    currentProductId() {
      return this.productId || this.$store?.state?.hotel?.setting?.productId || null
    },
    normalizedRoomTypeOptions() {
      return this.availableRoomTypes.map((room) => ({
        id: room.id,
        label: this.normalizeRoomName(room),
      }))
    },
    filteredDiscounts() {
      return this.discounts.filter((item) => {
        const typeOk = !this.filters.type || item.type === this.filters.type
        const operatorOk = !this.filters.operator || item.operator === this.filters.operator
        const periodStatus = this.getPeriodStatus(item)
        const periodOk = !this.filters.periodStatus || periodStatus === this.filters.periodStatus
        const stateOk = this.filters.state === '' || Number(item.state) === Number(this.filters.state)
        return typeOk && operatorOk && periodOk && stateOk
      })
    },
    sortedDiscounts() {
      const list = [...this.filteredDiscounts]
      if (!this.sortProp || !this.sortOrder) return list

      const direction = this.sortOrder === 'ascending' ? 1 : -1
      return list.sort((a, b) => {
        let result = 0
        if (this.sortProp === 'type') {
          result = this.getTypeSortValue(a.type) - this.getTypeSortValue(b.type)
        } else if (this.sortProp === 'minNight') {
          result = (Number(a.minNight) || 0) - (Number(b.minNight) || 0)
        } else if (this.sortProp === 'adjustValue') {
          result = this.getSignedValue(a) - this.getSignedValue(b)
        } else if (this.sortProp === 'periodStatus') {
          result = this.getPeriodSortValue(a) - this.getPeriodSortValue(b)
        }
        return result * direction
      })
    },
    paginatedDiscounts() {
      return this.sortedDiscounts
    },
    totalCount() {
      return Number(this.pagination.total) || 0
    },
  },
  created() {
    if (!this.currentProductId) return
    this.fetchRoomTypes(this.currentProductId)
    this.fetchData(this.currentProductId)
  },
  watch: {
    currentProductId: {
      handler(id) {
        if (!id) return
        this.fetchRoomTypes(id)
        this.fetchData(id)
      },
    },
  },
  methods: {
    normalizeRoomName(room) {
      return normalizeRoomName(room)
    },
    getRoomName(roomId) {
      const room = this.availableRoomTypes.find((r) => r.id === roomId)
      if (!room) return String(roomId)
      return this.normalizeRoomName(room) || String(roomId)
    },

    mapPriceRuleItem(item) {
      return mapPriceRuleItem(item)
    },
    mapPagination(pagination = {}) {
      return mapPagination(pagination, this.currentPage, this.pageSize)
    },
    buildFetchParams() {
      return buildFetchParams({
        filters: this.filters,
        currentPage: this.currentPage,
        pageSize: this.pageSize,
      })
    },
    buildApiPayload(form) {
      return buildApiPayload(form)
    },
    buildPatchPayload(current, original) {
      return buildPatchPayload(current, original)
    },

    async fetchData(productId = this.currentProductId) {
      this.loading = true
      try {
        const params = this.buildFetchParams()
        console.log('[LongStayPrice] fetchData payload:', { productId, ...params })
        const response = await getPriceRules(productId, params)
        const items = response?.items || response?.data?.items || []
        const pagination = response?.pagination || response?.data?.pagination || {}

        this.discounts = Array.isArray(items) ? items.map(this.mapPriceRuleItem) : []
        this.pagination = this.mapPagination(pagination)
        this.currentPage = this.pagination.currentPage
        this.pageSize = this.pagination.perPage
      } catch (error) {
        console.error(error)
        this.$message.error('取得房價調度列表失敗')
        this.discounts = []
      } finally {
        this.loading = false
      }
    },
    async fetchRoomTypes(productId = this.currentProductId) {
      try {
        console.log('[LongStayPrice] fetchRoomTypes payload:', { productId })
        const response = await getHotelRoomTypeOptions(productId)
        const items = response?.data || response?.items || response || []
        this.availableRoomTypes = Array.isArray(items)
          ? items.map((room) => ({
              id: room.id ?? room.value,
              name: room.label || room.name || room.roomTypeName || '',
              label: room.label || room.name || room.roomTypeName || '',
              categories: Array.isArray(room.categories) ? room.categories : [],
            }))
          : []
      } catch (error) {
        console.error(error)
        this.availableRoomTypes = []
      }
    },
    async handleSave(payload) {
      if (!this.currentProductId) return
      this.saving = true
      try {
        const apiPayload = this.buildApiPayload(payload)
        if (this.dialogMode === 'edit' && payload.id) {
          const patchPayload = this.buildPatchPayload(apiPayload, this.originalDialogApiModel || {})
          console.log('[LongStayPrice] updatePriceRule payload:', { productId: this.currentProductId, priceRuleId: payload.id, patchPayload })
          if (Object.keys(patchPayload).length > 0) {
            await updatePriceRule(this.currentProductId, payload.id, patchPayload)
          }
          this.$message.success('更新成功')
        } else {
          console.log('[LongStayPrice] createPriceRule payload:', { productId: this.currentProductId, apiPayload })
          await createPriceRule(this.currentProductId, apiPayload)
          this.$message.success('新增成功')
        }

        await this.fetchData(this.currentProductId)
      } catch (error) {
        console.error(error)
        this.$message.error('儲存失敗')
      } finally {
        this.saving = false
        this.dialogVisible = false
        this.originalDialogApiModel = null
      }
    },
    async handleDelete(row) {
      if (!this.currentProductId || !row?.id) return
      const itemName = row?.title || row?.name || '此項目'
      try {
        await this.$confirm(`確定要刪除${itemName}此房價調整嗎`, '提示', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'warning',
        })

        console.log('[LongStayPrice] deletePriceRule payload:', { productId: this.currentProductId, priceRuleId: row.id })
        await deletePriceRule(this.currentProductId, row.id)
        this.$message.success(`已刪除${itemName}`)
        await this.fetchData(this.currentProductId)
      } catch (error) {
        if (error === 'cancel') return
        console.error(error)
      }
    },
    async handleToggleState(row) {
      if (!this.currentProductId || !row?.id) return
      const nextState = Number(row.state) === 1 ? 1 : 0
      try {
        console.log('[LongStayPrice] updatePriceRule state payload:', { productId: this.currentProductId, priceRuleId: row.id, state: nextState })
        await updatePriceRule(this.currentProductId, row.id, { state: nextState })
        this.$message.success('狀態更新成功')
      } catch (error) {
        row.state = nextState === 1 ? 0 : 1
        console.error(error)
        this.$message.error('狀態更新失敗')
      }
    },

    openCreateDialog() {
      this.dialogMode = 'create'
      this.dialogFormModel = {
        ...defaultFormModel(),
        type: 'stay',
        dateRange: [],
      }
      this.originalDialogApiModel = null
      this.dialogVisible = true
    },
    async openEditDialog(row) {
      if (!this.currentProductId || !row?.id) return
      this.dialogMode = 'edit'
      this.saving = true
      try {
        const response = await getPriceRuleDetail(this.currentProductId, row.id)
        const detail = response?.data || response || {}
        const normalized = this.mapPriceRuleItem(detail)
        this.dialogFormModel = {
          ...defaultFormModel(),
          ...normalized,
          roomTypeIds: [...(normalized.roomTypeIds || [])],
          dateRange: normalized.validFrom && normalized.validUntil ? [normalized.validFrom, normalized.validUntil] : [],
        }
        this.originalDialogApiModel = this.buildApiPayload(this.dialogFormModel)
        this.dialogVisible = true
      } catch (error) {
        console.error(error)
        this.$message.error('取得優惠資料失敗')
      } finally {
        this.saving = false
      }
    },
    closeDialog() {
      this.dialogVisible = false
      this.originalDialogApiModel = null
    },

    onFiltersChange(nextFilters) {
      this.filters = {
        ...this.filters,
        ...nextFilters,
      }
      this.currentPage = 1
      if (!this.currentProductId) return
      this.fetchData(this.currentProductId)
    },
    onPageSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      if (!this.currentProductId) return
      this.fetchData(this.currentProductId)
    },
    onPageChange(page) {
      this.currentPage = page
      if (!this.currentProductId) return
      this.fetchData(this.currentProductId)
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop || ''
      this.sortOrder = order || ''
      this.currentPage = 1
    },

    getTypeSortValue(type) {
      return type === 'period' || type === 'date' ? 1 : 2
    },
    getSignedValue(row) {
      return getSignedValue(row)
    },
    getPeriodStatus(row) {
      return getPeriodStatus(row)
    },
    getPeriodSortValue(row) {
      return getPeriodSortValue(row)
    },
    isExpired(row) {
      return isExpired(row)
    },
  },
}
</script>

<template>
  <div :class="$style.page">
    <div :class="$style.topBar">
      <div>
        <div :class="$style.pageTitle">{{ uiText.pageTitle }}</div>
        <div :class="$style.pageSubTitle">{{ uiText.pageSubTitle }}</div>
      </div>
      <el-button type="primary" icon="el-icon-plus" class="is-round" :class="$style.createButton" @click="openCreateDialog">{{ uiText.createButton }}</el-button>
    </div>

    <div :class="$style.card">
      <PriceAdjustmentFilters
        :filters="filters"
        :type-options="filterOptions.type"
        :operator-options="filterOptions.operator"
        :period-status-options="filterOptions.periodStatus"
        :state-options="filterOptions.state"
        :placeholders="filterPlaceholders"
        @change="onFiltersChange"
      />

      <PriceAdjustmentTable
        :rows="paginatedDiscounts"
        :loading="loading"
        :total="totalCount"
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="pageSizes"
        :room-name-resolver="getRoomName"
        :type-text-map="typeTextMap"
        :labels="tableLabels"
        @edit="openEditDialog"
        @delete="handleDelete"
        @toggle-state="handleToggleState"
        @sort-change="handleSortChange"
        @page-size-change="onPageSizeChange"
        @page-change="onPageChange"
      />
    </div>

    <PriceAdjustmentDialog
      :visible="dialogVisible"
      :mode="dialogMode"
      :form-data="dialogFormModel"
      :room-type-options="normalizedRoomTypeOptions"
      :saving="saving"
      :labels="dialogLabels"
      :type-options="dialogTypeOptions"
      @save="handleSave"
      @cancel="closeDialog"
      @update:visible="dialogVisible = $event"
    />
  </div>
</template>

<style lang="scss" module scoped>
.page {
  padding: 20px;
  background: #f5f7fa;
}

.topBar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.pageTitle {
  font-size: 22px;
  font-weight: 700;
  color: #2c3e50;
}

.pageSubTitle {
  margin-top: 4px;
  color: #7f8c8d;
  font-size: 13px;
}

.createButton {
  box-shadow: none;
}

.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 20px;
}
</style>
